// Custom hook for consistent error handling across components
import { useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { errorHandler, ErrorCategory, ErrorSeverity, type AppError } from '@/utils/errorHandling';
import { logger } from '@/utils/logger';

interface UseErrorHandlerOptions {
  component?: string;
  showToast?: boolean;
  logLevel?: 'error' | 'warn' | 'info';
}

export const useErrorHandler = (options: UseErrorHandlerOptions = {}) => {
  const { toast } = useToast();
  const { component = 'Unknown', showToast = true, logLevel = 'error' } = options;

  const handleError = useCallback(
    (
      error: Error | string,
      errorOptions: {
        category?: ErrorCategory;
        severity?: ErrorSeverity;
        context?: Record<string, unknown>;
        userMessage?: string;
        silent?: boolean;
      } = {}
    ): AppError => {
      const {
        category = ErrorCategory.UNKNOWN,
        severity = ErrorSeverity.MEDIUM,
        context = {},
        userMessage,
        silent = false
      } = errorOptions;

      // Capture error with context
      const appError = errorHandler.captureError(error, {
        severity,
        category,
        context: {
          component,
          ...context
        }
      });

      // Log the error
      const logMessage = `Error in ${component}: ${appError.message}`;
      switch (logLevel) {
        case 'error':
          logger.error(logMessage, { component }, appError);
          break;
        case 'warn':
          logger.warn(logMessage, { component }, appError);
          break;
        case 'info':
          logger.info(logMessage, { component }, appError);
          break;
      }

      // Show toast notification if not silent
      if (showToast && !silent) {
        const displayMessage = userMessage || errorHandler.getUserFriendlyMessage(appError);
        
        toast({
          title: "Something went wrong",
          description: displayMessage,
          variant: severity === ErrorSeverity.CRITICAL || severity === ErrorSeverity.HIGH 
            ? "destructive" 
            : "default",
        });
      }

      return appError;
    },
    [component, showToast, logLevel, toast]
  );

  // Convenience methods for specific error types
  const handleAuthError = useCallback(
    (error: Error | string, context?: Record<string, unknown>) =>
      handleError(error, {
        category: ErrorCategory.AUTHENTICATION,
        severity: ErrorSeverity.HIGH,
        context,
        userMessage: "Please sign in again to continue."
      }),
    [handleError]
  );

  const handleNetworkError = useCallback(
    (error: Error | string, context?: Record<string, unknown>) =>
      handleError(error, {
        category: ErrorCategory.NETWORK,
        severity: ErrorSeverity.MEDIUM,
        context,
        userMessage: "Connection problem. Please check your internet and try again."
      }),
    [handleError]
  );

  const handleValidationError = useCallback(
    (error: Error | string, context?: Record<string, unknown>) =>
      handleError(error, {
        category: ErrorCategory.VALIDATION,
        severity: ErrorSeverity.LOW,
        context,
        userMessage: "Please check your input and try again."
      }),
    [handleError]
  );

  const handleFileUploadError = useCallback(
    (error: Error | string, context?: Record<string, unknown>) =>
      handleError(error, {
        category: ErrorCategory.FILE_UPLOAD,
        severity: ErrorSeverity.MEDIUM,
        context,
        userMessage: "File upload failed. Please try again with a different file."
      }),
    [handleError]
  );

  const handleDatabaseError = useCallback(
    (error: Error | string, context?: Record<string, unknown>) =>
      handleError(error, {
        category: ErrorCategory.DATABASE,
        severity: ErrorSeverity.HIGH,
        context,
        userMessage: "We're having trouble saving your data. Please try again."
      }),
    [handleError]
  );

  // Silent error logging (no toast)
  const logError = useCallback(
    (error: Error | string, context?: Record<string, unknown>) =>
      handleError(error, { silent: true, context }),
    [handleError]
  );

  return {
    handleError,
    handleAuthError,
    handleNetworkError,
    handleValidationError,
    handleFileUploadError,
    handleDatabaseError,
    logError,
  };
};